import React, { Dispatch, RefObject, SetStateAction, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Dropdown } from 'primereact/dropdown';
import { FlexBox } from '@eltoro-ui/components';
import { faArrowUp } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBallotCheck } from '@fortawesome/pro-light-svg-icons';
import TextArea from 'Pages/CampaignCreation/components/GeneratedCampaign/GeneratedCampaignContent/EditWithAIModal/TextArea';
import { sendPrompt } from 'Requests/Request_Methods/campaignMethods';
import { AIGenerationResponseType } from 'types';
import '../GeneratedCreatives.scss';

type AIPromptContainerProps = {
  onFocus: (promptValue: AIGenerationResponseType | null, success: boolean) => void;
  textAreaRef: RefObject<HTMLTextAreaElement>;
  setConversationData: Dispatch<SetStateAction<AIGenerationResponseType[]>>;
  setPendingAnswer: Dispatch<SetStateAction<boolean>>;
  setMaxSizeReached: Dispatch<SetStateAction<boolean>>;
  setPromptData: Dispatch<SetStateAction<string>>;
  setHasErr: Dispatch<SetStateAction<boolean>>;
  setHasAiErr: Dispatch<SetStateAction<boolean>>;
  banner_uuid?: string;
  disableSend: boolean;
};

const AIPromptContainer = ({
  onFocus,
  textAreaRef,
  setConversationData,
  setPendingAnswer,
  setMaxSizeReached,
  setPromptData,
  setHasErr,
  banner_uuid,
  setHasAiErr,
  disableSend,
}: AIPromptContainerProps) => {
  const { campaignId } = useParams<{ campaignId: string }>();

  const [promptValue, setPromptValue] = useState('');
  const [visiblePromptValue, setVisiblePromptValue] = useState('');

  const onPromptClick = () => {
    if (banner_uuid) {
      setPromptData(promptValue);
      setPendingAnswer(true);
      sendPrompt(campaignId, banner_uuid, promptValue)
        .then((res: any) => {
          if (res.data) {
            setConversationData(prevState => [...prevState, res.data]);
          }
        })
        .catch(err => {
          if (err.detail && err.detail === 'Max prompts reached') {
            setMaxSizeReached(true);
          } else if (
            err.detail &&
            (err.detail === 'Invalid response from AI' ||
              err.detail === 'Invalid response from AI in json')
          ) {
            setHasAiErr(true);
          } else {
            setHasErr(true);
          }
        })
        .finally(() => {
          setPendingAnswer(false);
          setPromptValue('');
        });
    }
  };
  const handlePromptClick = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent> | React.KeyboardEvent<HTMLTextAreaElement>
  ) => {
    e.preventDefault();
    setVisiblePromptValue('');
    setHasErr(false);
    onPromptClick();
  };

  const groupedActions = [
    {
      label: 'Fonts/wording',
      items: [
        { label: 'Change font style to...', value: 'Change font style to ' },
        { label: 'Change font size to...', value: 'Change font size to ' },
        { label: 'Change font color to...', value: 'Change font color to ' },
        { label: 'Update address to...', value: 'Update address to ' },
      ],
    },
    {
      label: 'Bed/bath/sqft',
      items: [
        { label: 'Change bed count to...', value: 'Change bed count to ' },
        { label: 'Change bath count to...', value: 'Change bath count to ' },
        { label: 'Change sqft to...', value: 'Change sqft to ' },
      ],
    },
    {
      label: 'Button',
      items: [
        { label: 'Change button text to...', value: 'Change button text to ' },
        {
          label: 'Change button background color to... ',
          value: 'Change button background color to ',
        },
        { label: 'Move button to...', value: 'Move button to ' },
      ],
    },
    {
      label: 'Background/Colors',
      items: [{ label: 'Change background color to...', value: 'Change background color to ' }],
    },
  ];

  const groupedItemTemplate = (label: string) => {
    return (
      <div className="flex align-items-center">
        <div>{label}</div>
      </div>
    );
  };

  const handleSelect = (selected: string) => {
    setPromptValue(prev => (prev ? `${prev}; ${selected}` : selected));
    setVisiblePromptValue(prev => (prev ? `${prev}; ${selected}` : selected));
    setTimeout(() => {
      onFocus(null, true);
    }, 150);
  };
  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey && promptValue.trim()) {
      handlePromptClick(event);
    }
  };

  return (
    <FlexBox flexDirection="column" UNSAFE_className="prompt-container">
      <form>
        <TextArea
          textAreaRef={textAreaRef}
          promptValue={visiblePromptValue}
          setPromptValue={value => {
            setPromptValue(value);
            setVisiblePromptValue(value);
          }}
          onEnter={handleKeyDown}
        />
        <FlexBox UNSAFE_className="prompts" alignItems="center" justifyContent="space-between">
          <FlexBox gap="8px" alignItems="center">
            <FontAwesomeIcon icon={faBallotCheck} color="#007B94" style={{ fontSize: '16px' }} />
            <Dropdown
              value="Prompt Library"
              filter
              onChange={e => {
                handleSelect(e.target.value);
              }}
              options={groupedActions}
              optionLabel="label"
              optionGroupLabel="label"
              optionGroupChildren="items"
              optionGroupTemplate={option => groupedItemTemplate(option.label)}
              placeholder="Prompt Library"
              className="prompt-dropdown"
              filterPlaceholder="Search for prompts..."
              panelClassName="prompt-dropdown-panel"
            />
          </FlexBox>
          <button
            className="send"
            disabled={!promptValue.trim().length || disableSend}
            onClick={handlePromptClick}
          >
            <span className="icon-wrapper">
              <FontAwesomeIcon icon={faArrowUp} color="#fff" style={{ fontSize: '14px' }} />
            </span>
          </button>
        </FlexBox>
      </form>
    </FlexBox>
  );
};
export default AIPromptContainer;
